import { Injectable } from '@angular/core'
import { SelectionModel } from '@angular/cdk/collections'
import * as R from 'ramda'
import { map, observeOn } from 'rxjs/operators'
import { TableColumn } from '@tv3/interfaces/table/table-column'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { LocalStorageKeys } from '@tv3/enums/local-storage-keys'
import { UserStorage } from './storage.service'
import { asapScheduler, asyncScheduler } from 'rxjs'
import { get } from 'lodash'
import { MatTableDataSource } from '@angular/material/table'
import { ITableService } from '@tokeet-frontend/utility'

@Injectable({
  providedIn: 'root',
})
export class TableService implements ITableService {
  tableColumns = {
    [TableType.RentalsList]: [],
    [TableType.GuestsList]: [],
    [TableType.CustomCodesList]: [],
    [TableType.InquiriesList]: [],
    [TableType.InvoiceTemplates]: [],
    [TableType.InvoiceAssociatedRules]: [],
    [TableType.InvoiceRuleActivityLog]: [],
    [TableType.InvoiceRules]: [],
    [TableType.Invoices]: [],
    [TableType.Expenses]: [],
    [TableType.Tasks]: [],
  }

  constructor(private storage: UserStorage) {}

  availableColumns: { [id: string]: TableColumn[] } = {
    [TableType.RentalsList]: [
      { name: 'select', label: 'Select', restricted: true },
      { name: 'status', label: 'Status' },
      { name: 'name', label: 'Name' },
      { name: 'description', label: 'Description' },
      { name: 'phone', label: 'Phone' },
      { name: 'cityView', label: 'City' },
      { name: 'countryView', label: 'Country' },
      { name: 'edit', label: 'Edit', restricted: true },
    ],
    [TableType.GuestsList]: [
      { name: 'select', label: 'Select', restricted: true },
      { name: 'name', label: 'Guest Name' },
      { name: 'primaryEmail', label: 'Email' },
      { name: 'phone', label: 'Telephone' },
      { name: 'countryView', label: 'Country' },
      { name: 'lastMessage', label: 'Last Message' },
      { name: 'edit', label: 'Edit', restricted: true },
    ],
    [TableType.InquiriesList]: [
      { name: 'select', label: 'Select', restricted: true },
      { name: 'created', label: 'Created' },
      { name: 'inquirySource', label: 'Source' },
      { name: 'rentalView', label: 'Rental' },
      { name: 'touch', label: 'Updated' },
      { name: 'receivedOn', label: 'Received' },
      { name: 'guestNameView', label: 'Guest Name' },
      { name: 'guestArrive', label: 'Arrive' },
      { name: 'guestDepart', label: 'Depart' },
      { name: 'nightsView', label: 'Nights' },
      { name: 'guestsView', label: 'Guests' },
      { name: 'actions', label: 'Actions' },
    ],
    [TableType.CustomCodesList]: [
      { name: 'select', label: 'Select', restricted: true },
      { name: 'name', label: 'Name' },
      { name: 'description', label: 'Description' },
      { name: 'body', label: 'Value' },
      { name: 'creatorView', label: 'Creator' },
      { name: 'created', label: 'Created' },
      { name: 'rentalNames', label: 'Rental' },
      { name: 'tagView', label: 'Tags' },
      { name: 'edit', label: 'Edit', restricted: true },
    ],
    [TableType.Invoices]: [
      { name: 'created', label: 'Created' },
      { name: 'invoiceDate', label: 'Invoice Date' },
      { name: 'dueDate', label: 'Due Date' },
      { name: 'arriveDate', label: 'Arrive Date' },
      { name: 'departDate', label: 'Depart Date' },
      { name: 'sent', label: 'Sent' },
      { name: 'invoiceNum', label: 'Invoice ID' },
      { name: 'guestName', label: 'Guest Name' },
      { name: 'rentalName', label: 'Rental' },
      { name: 'invoiceStatus', label: 'Status' },
      { name: 'invoiceTotal', label: 'Amount' },
      { name: 'edit', label: 'Edit', restricted: true },
    ],
    [TableType.Expenses]: [
      { name: 'select', label: 'Select', restricted: true },
      { name: 'date', label: 'Expense Date' },
      { name: 'due', label: 'Due Date' },
      { name: 'rentalView', label: 'Rental' },
      { name: 'category', label: 'Category' },
      { name: 'amount', label: 'Amount' },
      { name: 'methodView', label: 'Method' },
      { name: 'statusView', label: 'Status' },
      { name: 'link', label: 'Photo' },
      { name: 'createdByView', label: 'Created By' },
      { name: 'edit', label: 'Edit', restricted: true },
    ],
    [TableType.InvoiceTemplates]: [
      { name: 'select', label: 'Select', restricted: true },
      { name: 'name', label: 'Name' },
      { name: 'description', label: 'Description' },
      { name: 'template.subject', label: 'Subject' },
      { name: 'created', label: 'Created' },
      { name: 'lastUsedView', label: 'Last Used' },
      { name: 'edit', label: 'Edit', restricted: true },
    ],
    [TableType.InvoiceRules]: [
      { name: 'select', label: 'Select', restricted: true },
      { name: 'attributes.name', label: 'Rentals' },
      { name: 'created', label: 'Channels' },
      { name: 'eventView', label: 'Event' },
      { name: 'templateView.name', label: 'Created' },
      { name: 'charge', label: 'Charge Invoice' },
      { name: 'send', label: 'Send Invoice' },
      { name: 'mark', label: 'Mark As Paid' },
      { name: 'lastusedView', label: 'Modified' },
      { name: 'status', label: 'Status' },
      { name: 'edit', label: 'Edit', restricted: true },
    ],
    [TableType.InvoiceAssociatedRules]: [
      { name: 'attributes.name', label: 'Rentals' },
      { name: 'created', label: 'Channels' },
      { name: 'eventView', label: 'Subject' },
      { name: 'charge', label: 'Charge Invoice' },
      { name: 'send', label: 'Send Invoice' },
      { name: 'mark', label: 'Mark As Paid' },
      { name: 'lastusedView', label: 'Modified' },
      { name: 'edit', label: 'Edit', restricted: true },
    ],
    [TableType.InvoiceRuleActivityLog]: [
      { name: 'sentView', label: 'Date' },
      { name: 'guest_name', label: 'Guest' },
      { name: 'eventView', label: 'Action' },
      { name: 'templateView', label: 'Template' },
      { name: 'channel_name', label: 'Channel' },
    ],
    [TableType.Tasks]: [
      { name: 'finish', label: 'Finish' },
      { name: 'created', label: 'Created' },
      { name: 'name', label: 'Name' },
      { name: 'userView', label: 'User' },
      { name: 'due', label: 'Due' },
      { name: 'inquiryView', label: 'Inquiry' },
      { name: 'edit', label: 'Edit', restricted: true },
    ],
  }

  masterToggle<T>(
    selection: SelectionModel<T>,
    renderedData: T[],
    source: MatTableDataSource<T>,
    isRowSelectable: (row: T) => boolean = () => true,
    idPropName?: string
  ) {
    if (this.isAllSelected(selection, renderedData, isRowSelectable)) {
      selection.clear()
    } else {
      source.data.forEach((row) => {
        if (!isRowSelectable(row)) return

        if (
          !R.isNil(
            R.find((rendered) => {
              if (idPropName) {
                return rendered[idPropName] === row[idPropName]
              } else if (R.has('id', rendered)) {
                return rendered['id'] === row['id']
              } else if (R.has('pkey', rendered)) {
                return rendered['pkey'] === row['pkey']
              } else if (R.has('propertyId', rendered)) {
                return rendered['propertyId'] === row['propertyId']
              } else if (!R.isNil(rendered['rateId'])) {
                return rendered['rateId'] === row['rateId']
              }
            }, renderedData)
          )
        ) {
          selection.select(row)
        }
      })
    }
  }

  isAllSelected<T>(selection: SelectionModel<T>, renderedData: T[], isRowSelectable: (row: T) => boolean = () => true) {
    const selectedItems = R.filter(isRowSelectable, selection.selected)
    const renderedItems = R.filter(isRowSelectable, renderedData)

    return selectedItems.length === renderedItems.length
  }

  isEmptyTable(source: any, immediate = false) {
    return source['_renderData'].pipe(
      map((rows: any) => (rows || []).length === 0),
      observeOn(immediate ? asapScheduler : asyncScheduler)
    )
  }

  saveColumns(type: TableType, columns: TableColumn[]) {
    const existing = this.storage.get(LocalStorageKeys.TABLE_COLUMNS, this.tableColumns)

    this.storage.set(LocalStorageKeys.TABLE_COLUMNS, R.merge(existing, { [type]: columns }))
  }

  getColumns(type: TableType) {
    const data = this.storage.get(LocalStorageKeys.TABLE_COLUMNS, this.tableColumns)
    return data[type] || this.tableColumns[type]
  }

  getAvailableColumns(type: TableType): TableColumn[] {
    const currentColumns = this.getColumns(type)

    const sorted = R.sort((a: TableColumn, b: TableColumn) => {
      const firstIndex = R.findIndex(R.propEq('name', a.name))(currentColumns)
      const secondIndex = R.findIndex(R.propEq('name', b.name))(currentColumns)

      // when new item is added but not present in local storage
      // we want to display it after Select column, not before
      if (a.name === 'select' && secondIndex === -1) {
        return -1
      } else {
        return firstIndex - secondIndex
      }
    }, this.availableColumns[type])

    const excluded = <string[]>R.pipe(
      R.filter((a: TableColumn) => a.excluded),
      <any>R.map((x: TableColumn) => x.name)
    )(currentColumns)
    const included = <string[]>R.pipe(
      R.filter((a: TableColumn) => !a.excluded),
      <any>R.map((x: TableColumn) => x.name)
    )(currentColumns)

    return <TableColumn[]>R.map((c) => {
      if (R.contains(c.name, excluded)) {
        return { ...c, excluded: true }
      } else if (R.contains(c.name, included)) {
        return { ...c, excluded: false }
      } else {
        return c
      }
    }, sorted)
  }

  getDisplayedColumns(type: TableType, excludeEdit = false): string[] {
    const result = <string[]>R.pipe(
      R.filter((c: TableColumn) => !c.excluded),
      <any>R.map((c: TableColumn) => c.name),
      <any>R.filter((c: string) => c !== 'edit'),
      <any>R.append('edit'),
      R.when(R.contains('select'), R.pipe(<any>R.reject((c) => c === 'select'), R.prepend('select')))
    )(<any>this.getAvailableColumns(type))

    if (excludeEdit) {
      return R.reject((c) => c === 'edit', result)
    }

    return result
  }

  normalizeString(value: any) {
    return (value || '')
      .toString()
      .replace(/\s{2,}/g, ' ')
      .trim()
  }

  filterPredicateFactory(searchableKeys?: string[]) {
    searchableKeys = searchableKeys || []
    return (data, term) => {
      const keys = searchableKeys.length ? [...searchableKeys] : Object.keys(data)
      let dataStr = keys.reduce((acc, key) => {
        let value = data[key]
        if (key && key.indexOf('.') !== -1) {
          value = get(data, key)
        }
        return `${acc}◬${value || ''}`
      }, '')

      term = this.normalizeString(term).toLowerCase()
      dataStr = this.normalizeString(dataStr).toLowerCase()

      return dataStr.indexOf(term) !== -1
    }
  }
}
